import axios from 'axios';

// API設定 - 環境変数から取得
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api-pheuu73roa-an.a.run.app';
const API_KEY = import.meta.env.VITE_API_KEY || '2ViqpA6E&85qbgtL!Hk3t8o8Zxd&qhukXQjhHaKhS%&uw$Ud#x&TH5goWMQE7Y@!uW*HML5Pr4nGizBg3JsHNr6$ULvZj@Vve7BEdyL!R%2VSuPDgf$kcWAfTrxv4K*Z';

console.log('API Configuration:', {
  API_BASE_URL,
  API_KEY: API_KEY ? '***' + API_KEY.slice(-10) : 'NOT SET'
});
// 共通のAxiosインスタンスを作成（セッションCookie認証 + 旧APIキー認証の並行運用）
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'x-api-key': API_KEY  // 後方互換のため残す（段階的に削除予定）
  },
  withCredentials: true  // セッションCookieを送信
});

/**
 * アプリIDを生成して取得
 */
export const getNewAppId = async () => {
  try {
    const response = await apiClient.get('/getAppId');
    return response.data.appId;
  } catch (error) {
    console.error('Error getting new app ID:', error);
    throw error;
  }
};

/**
 * アプリを作成する
 * @param {string} recipeText - アプリ作成のレシピ
 * @param {string} userId - ユーザーID
 * @param {string} appId - アプリID
 * @param {string} userName - LINEの表示名
 * @param {string} shared - 共有アプリかどうか（"true": 友達と共有, "false": 自分専用）
 */
export const createApp = async (recipeText, userId, appId, userName = '', shared = "true") => {
  try {
    const response = await apiClient.post('/createApp', {
      recipeText,
      userId,
      userName,
      appId,
      shared
    });
    return response.data;
  } catch (error) {
    console.error('Error creating app:', error);

    // 1日の作成上限に達した場合のエラーハンドリング
    if (error.response && error.response.status === 429) {
      const errorData = error.response.data;
      if (errorData.error === 'DAILY_LIMIT_EXCEEDED') {
        throw new Error(`DAILY_LIMIT_EXCEEDED:${errorData.message}`);
      }
    }

    // 利用上限エラー・決済エラー（402）
    if (error.response && error.response.status === 402) {
      const data = error.response.data || {};
      console.log('402 Payment Required error detected:', data);

      // 決済失敗エラー
      if (data.error === 'PAYMENT_FAILED') {
        const err = new Error(data.message || 'お支払いに問題があります');
        err.code = 'PAYMENT_FAILED';
        err.status = data.status;
        err.plan = data.plan;
        console.log('Throwing PAYMENT_FAILED error:', err);
        throw err;
      }

      // 利用上限エラー
      const err = new Error(data.error || 'LIMIT_EXCEEDED');
      err.code = 'LIMIT_EXCEEDED';
      err.billingInfo = {
        plan: data.plan || 'unknown',
        monthlyQuota: data.monthlyQuota || 0,
        usedThisPeriod: data.usedThisPeriod || 0,
        extraCredits: data.extraCredits || 0,
        currentPeriodEnd: data.currentPeriodEnd
      };
      console.log('Throwing LIMIT_EXCEEDED error with billingInfo:', err.billingInfo);
      throw err;
    }

    // コンプライアンス判定によるブロック（400）
    if (error.response && error.response.status === 400) {
      const data = error.response.data || {};
      if (data.error === 'COMPLIANCE_DENIED' || data.error === 'COMPLIANCE_CHECK_FAILED') {
        const e = new Error(data.message || 'コンプライアンス確認により作成を中断しました');
        e.code = 'COMPLIANCE';
        e.categories = Array.isArray(data.categories) ? data.categories : [];
        e.userFriendly = true;
        throw e;
      }
    }

    throw error;
  }
};

/**
 * ユーザーの最新アプリを取得
 */
export const getLatestApps = async (userId) => {
  try {
    const response = await apiClient.get(`${API_BASE_URL}/getLatestApp?userId=${userId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching latest apps:', error);
    throw error;
  }
};

/**
 * 特定IDのアプリを取得
 * @param {string} appId - アプリID
 * @param {string} userId - ユーザーID（アクセス制御用、オプション）
 */
export const getAppById = async (appId, userId = null) => {
  try {
    const url = userId
      ? `${API_BASE_URL}/getApp?id=${appId}&userId=${userId}`
      : `${API_BASE_URL}/getApp?id=${appId}`;
    const response = await apiClient.get(url);
    return response.data;
  } catch (error) {
    // Return null on 404 so callers can treat as not-found gracefully
    if (error?.response?.status === 404) {
      return null;
    }

    // トライアル期限切れエラー（403）
    if (error?.response?.status === 403) {
      const data = error.response.data || {};
      if (data.code === 'TRIAL_EXPIRED') {
        const err = new Error(data.message || 'このアプリは利用期限が切れました。');
        err.code = 'TRIAL_EXPIRED';
        err.expiresAt = data.expiresAt;
        err.plan = data.plan;
        throw err;
      }
    }

    console.error('Error fetching app:', error);
    throw error;
  }
};

// runIdを指定してアプリを取得
export const getAppByRunId = async (runId) => {
  try {
    const response = await apiClient.get(`${API_BASE_URL}/getAppByRunId?runId=${runId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching app by runId:', error);
    throw error;
  }
};

/**
 * アプリを削除
 */
export const deleteApp = async (appId, userId) => {
  try {
    const response = await apiClient.delete(`${API_BASE_URL}/deleteApp?id=${appId}&userId=${userId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting app:', error);
    throw error;
  }
};

/**
 * アプリにいいねを追加
 */
export const likeApp = async (appId) => {
  try {
    const response = await apiClient.post(`${API_BASE_URL}/likeApp`, { appId });
    return response.data;
  } catch (error) {
    console.error('Error liking app:', error);
    throw error;
  }
};

// 公開表示名の取得
export const getUserPublicName = async (userId) => {
  try {
    const res = await apiClient.get(`${API_BASE_URL}/getUserPublicName?userId=${encodeURIComponent(userId)}`);
    return res.data?.publicName || '';
  } catch (e) {
    console.error('Error getUserPublicName:', e);
    return '';
  }
};

// 公開表示名の保存
export const setUserPublicName = async (userId, publicName) => {
  try {
    await apiClient.post(`${API_BASE_URL}/setUserPublicName`, { userId, publicName });
    return true;
  } catch (e) {
    console.error('Error setUserPublicName:', e);
    return false;
  }
};

/**
 * アプリのいいねを取り消す
 */
export const removeLike = async (appId, userId) => {
  try {
    const response = await apiClient.post(`${API_BASE_URL}/removeLike`, { appId, userId });
    return response.data;
  } catch (error) {
    console.error('Error removing like:', error);
    throw error;
  }
};

/**
 * 使用状況を取得
 */
export const getBillingUsage = async (userId) => {
  try {
    const response = await apiClient.get(`/billing/usage?userId=${userId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching billing usage:', error);
    throw error;
  }
};

/**
 * サブスクリプションCheckoutセッションを作成
 * @param {string} userId - ユーザーID
 * @param {string} plan - 'light' または 'creator'
 */
export const createSubscriptionSession = async (userId, plan) => {
  try {
    const response = await apiClient.post('/billing/create-subscription-session', {
      userId,
      plan
    });
    return response.data.url; // Stripe Checkout URL
  } catch (error) {
    console.error('Error creating subscription session:', error);
    throw error;
  }
};

/**
 * 追加クレジットCheckoutセッションを作成
 */
export const createTopupSession = async (userId) => {
  try {
    const response = await apiClient.post('/billing/create-topup-session', {
      userId
    });
    return response.data.url;
  } catch (error) {
    console.error('Error creating topup session:', error);
    throw error;
  }
};

/**
 * Customer Portalセッションを作成
 */
export const createCustomerPortalSession = async (userId) => {
  try {
    const response = await apiClient.post('/billing/customer-portal', {
      userId
    });
    return response.data.url;
  } catch (error) {
    console.error('Error creating customer portal session:', error);
    throw error;
  }
};

/**
 * サブスクリプションのプラン変更（次回更新日に適用）
 * @param {string} userId - ユーザーID
 * @param {string} newPlan - 新しいプラン名（'light' または 'creator'）
 * @returns {Promise<{success: boolean, effectiveDate: string}>}
 */
export const changeSubscriptionPlan = async (userId, newPlan) => {
  try {
    const response = await apiClient.post('/billing/change-plan', {
      userId,
      newPlan
    });
    return response.data;
  } catch (error) {
    console.error('Error changing plan:', error);
    throw error;
  }
};
